import type {
  EconomicEvent,
  EconomicCalendarFilterOptions,
} from "@/vybe-trader-client/economic-calendar/types";
import {
  eachDayOfInterval,
  endOfWeek,
  format,
  startOfWeek,
} from "date-fns";

/** Local yyyy-MM-dd — used consistently for picker + grouping (see plan: document local policy). */
export function calendarDayKeyLocal(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function stableDayKeyFromEvent(event: EconomicEvent): string {
  const raw = event.date;
  const head = raw.includes("T") ? raw.split("T")[0] : raw.slice(0, 10);
  return head;
}

/** Count events per yyyy-MM-dd (local keys aligned with ISO date portion from API payloads). */
export function buildEventsCountByDay(events: EconomicEvent[]): Map<string, number> {
  const map = new Map<string, number>();
  for (let i = 0; i < events.length; i++) {
    const k = stableDayKeyFromEvent(events[i]);
    map.set(k, (map.get(k) ?? 0) + 1);
  }
  return map;
}

export type CalendarDisplayScope = "day" | "week";

export function getWeekBoundsLocal(anchor: Date): { start: Date; end: Date } {
  const start = startOfWeek(anchor, { weekStartsOn: 0 });
  const end = endOfWeek(anchor, { weekStartsOn: 0 });
  return { start, end };
}

export function enumerateWeekDaysLocal(anchor: Date): Date[] {
  const { start, end } = getWeekBoundsLocal(anchor);
  return eachDayOfInterval({ start, end });
}

/** Match event name/currency substring (ASCII case-fold). */
export function eventMatchesSearch(event: EconomicEvent, q: string): boolean {
  const t = q.trim().toLowerCase();
  if (!t) return true;
  return (
    event.name.toLowerCase().includes(t) ||
    event.currency.toLowerCase().includes(t)
  );
}

export function applyEconomicCalendarFilters(
  events: EconomicEvent[],
  filters: EconomicCalendarFilterOptions,
  opts: {
    search: string;
    highImpactOnly?: boolean;
  }
): EconomicEvent[] {
  let next = events;
  if (opts.highImpactOnly) {
    next = next.filter((e) => e.impact === "High");
  }
  if (filters.currencies.length > 0) {
    next = next.filter((e) => filters.currencies.includes(e.currency));
  }
  if (filters.impacts.length > 0) {
    next = next.filter((e) => filters.impacts.includes(e.impact));
  }
  if (opts.search.trim()) {
    next = next.filter((e) => eventMatchesSearch(e, opts.search));
  }
  return next;
}

/** Events for a calendar day bucket (already filtered upstream if desired). */
export function sliceEventsForLocalDay(events: EconomicEvent[], dayKey: string) {
  return events.filter((e) => stableDayKeyFromEvent(e) === dayKey);
}

/** Events overlapping local week inclusive of anchors [start,end] day keys */
export function sliceEventsForLocalWeek(events: EconomicEvent[], anchorDate: Date) {
  const { start, end } = getWeekBoundsLocal(anchorDate);
  const from = calendarDayKeyLocal(start);
  const to = calendarDayKeyLocal(end);
  return events.filter((e) => {
    const k = stableDayKeyFromEvent(e);
    return k >= from && k <= to;
  });
}

export function formatWeekdayMonthDay(d: Date): string {
  return format(d, "EEE M/d");
}

export type AgendaSortColumn = "time" | "impact" | "currency" | "name";

const impactRank: Record<string, number> = { High: 0, Medium: 1, Low: 2 };

/** Sort descending impact by default preference when grouping by column */
export function sortEventsForAnalysis(
  events: EconomicEvent[],
  column: AgendaSortColumn,
  direction: "asc" | "desc"
): EconomicEvent[] {
  const sorted = [...events];
  const m = direction === "asc" ? 1 : -1;
  sorted.sort((a, b) => {
    switch (column) {
      case "time": {
        const ta = new Date(a.dateTimeISO).getTime();
        const tb = new Date(b.dateTimeISO).getTime();
        return ta === tb ? a.name.localeCompare(b.name) : (ta - tb) * m;
      }
      case "impact": {
        const ra = impactRank[a.impact] ?? 99;
        const rb = impactRank[b.impact] ?? 99;
        return ra === rb
          ? new Date(a.dateTimeISO).getTime() -
              new Date(b.dateTimeISO).getTime()
          : (ra - rb) * m;
      }
      case "currency": {
        const c = a.currency.localeCompare(b.currency);
        return c === 0
          ? new Date(a.dateTimeISO).getTime() -
              new Date(b.dateTimeISO).getTime()
          : c * m;
      }
      case "name": {
        const n = a.name.localeCompare(b.name);
        return n === 0
          ? new Date(a.dateTimeISO).getTime() -
              new Date(b.dateTimeISO).getTime()
          : n * m;
      }
      default:
        return 0;
    }
  });
  return sorted;
}
